import type { IntlShape } from 'react-intl';
import { deriveActiveTabIdFromUrl } from 'features/Chat/derive-chat-sidebar-tab';
import type { NavItemType } from 'types';
import { getPathnameOnly } from 'utils/chat-workflow-url';
import type { ChatSidebarScopeTab } from 'utils/chat-sidebar-unified-statuses';
import {
  BUILTIN_UNASSIGNED_FOLDER_ID,
  type ChatSidebarFolder,
  type ViewFolderMap,
} from 'utils/chat-sidebar-folders-storage';
import { isAwaitingCustomerSendFailedFilterActive } from 'utils/awaiting-customer-send-failed';

/** ברירות מחדל בעברית לכותרות הקשר (כשאין מפתח ב-locales) */
export const CHAT_VIEW_CONTEXT_TITLE_DEFAULTS: Record<string, string> = {
  'sidebar.nav.views.allConversationsRow': 'כל השיחות',
  'sidebar.nav.views.context.custom': 'תצוגה מותאמת',
  'sidebar.nav.views.context.default': 'שיחות',
  'sidebar.nav.views.context.unassignedPendingAssign': 'לשיוך לנציג',
  'sidebar.nav.views.context.awaitingReply': 'להמשך מענה',
  'sidebar.nav.views.context.firstReply': 'למענה ראשוני',
  'sidebar.nav.views.context.awaitingCustomerShort': 'לאיש קשר',
  'sidebar.nav.views.context.awaitingCustomerSendFailed': 'לאיש קשר — שלא נשלחה',
  'sidebar.nav.views.context.reminderByDate': 'בהקפאה',
  'sidebar.nav.views.context.completedArchive': 'הסתיימו',
  'sidebar.nav.views.context.notMyFull': 'שיחות של אחרים',
  'sidebar.nav.views.context.botsFull': 'בוטים',
  'sidebar.nav.views.context.allFull': 'כל השיחות',
  'sidebar.nav.views.context.team.unassignedPendingAssign': 'לשיוך לנציג (כל המשתמשים)',
  'sidebar.nav.views.context.team.awaitingReply': 'להמשך מענה (כל המשתמשים)',
  'sidebar.nav.views.context.team.firstReply': 'למענה ראשוני (כל המשתמשים)',
  'sidebar.nav.views.context.team.awaitingCustomerShort': 'לאיש קשר (כל המשתמשים)',
  'sidebar.nav.views.context.team.awaitingCustomerSendFailed': 'לאיש קשר — שלא נשלחה (כל המשתמשים)',
  'sidebar.nav.views.context.team.reminderByDate': 'בהקפאה (כל המשתמשים)',
  'sidebar.nav.views.context.team.completedArchive': 'הסתיימו (כל המשתמשים)',
  'sidebar.nav.views.context.team.allFull': 'כל השיחות (צוות)',
};

export const CHAT_VIEW_FOLDER_TITLE_DEFAULTS: Record<string, string> = {
  'sidebar.nav.chatScopeTabs.mine': 'השיחות שלי',
  'sidebar.nav.chatScopeTabs.team': 'צוות',
  'sidebar.nav.chatScopeTabs.all': 'כל השיחות',
  'sidebar.nav.views.unassigned': 'לא משויכות',
  'sidebar.nav.views.botsFolder': 'בוטים',
  'sidebar.nav.views.customViewsFolder': 'מותאמות',
};

export type ChatFolderTitle =
  | { type: 'locale'; id: string; defaultMessage: string }
  | { type: 'text'; text: string };

function wfContextId(wf: string | null, team: boolean): string | null {
  if (!wf) return null;
  const prefix = team ? 'sidebar.nav.views.context.team.' : 'sidebar.nav.views.context.';
  switch (wf) {
    case '1':
      return `${prefix}unassignedPendingAssign`;
    case '2':
      return `${prefix}firstReply`;
    case '3':
      return `${prefix}awaitingCustomerShort`;
    case '4':
      return `${prefix}awaitingReply`;
    case '5':
      return `${prefix}reminderByDate`;
    case '6':
      return `${prefix}completedArchive`;
    default:
      return null;
  }
}

/**
 * מזהה מפתח תרגום לכותרת הקשר מעל רשימת השיחות (נתיב + wf + audience).
 */
export function getChatViewContextTitleId(pathname: string, search: string): string {
  const q = search.startsWith('?') ? search.slice(1) : search;
  const sp = new URLSearchParams(q);
  const wf = sp.get('wf');
  const isTeam = sp.get('audience') === 'team';
  const isSendFailedFilter = isAwaitingCustomerSendFailedFilterActive(search);

  const wfTitleId = (team: boolean): string | null => {
    if (isSendFailedFilter) {
      return team
        ? 'sidebar.nav.views.context.team.awaitingCustomerSendFailed'
        : 'sidebar.nav.views.context.awaitingCustomerSendFailed';
    }
    return wfContextId(wf, team);
  };

  if (pathname.startsWith('/chats/custom')) {
    return 'sidebar.nav.views.context.custom';
  }

  if (pathname.startsWith('/chats/unassigned')) {
    return isTeam
      ? 'sidebar.nav.views.context.team.unassignedPendingAssign'
      : 'sidebar.nav.views.context.unassignedPendingAssign';
  }

  if (pathname.startsWith('/chats/my/snoozed')) {
    return 'sidebar.nav.views.context.reminderByDate';
  }

  if (pathname.startsWith('/chats/my')) {
    return wfTitleId(false) ?? 'sidebar.nav.views.allConversationsRow';
  }

  if (pathname.startsWith('/chats/not-my')) {
    return wfTitleId(true) ?? 'sidebar.nav.views.context.notMyFull';
  }

  if (pathname.startsWith('/chats/bots')) {
    return 'sidebar.nav.views.context.botsFull';
  }

  if (pathname.startsWith('/chats/completed')) {
    return isTeam
      ? 'sidebar.nav.views.context.team.completedArchive'
      : 'sidebar.nav.views.context.completedArchive';
  }

  //* All-scope. Every other scope (custom/unassigned/my/not-my/bots/completed) has already
  //* returned above, so any remaining '/chats/...' path is all-scope with a conversation
  //* selected (e.g. '/chats/<phone>'). Match it with startsWith — not an exact '/chats'
  //* equality — otherwise opening a chat drops the wf title back to the generic default.
  if (pathname === '/chats' || pathname.startsWith('/chats/')) {
    if (isTeam) {
      return wfTitleId(true) ?? 'sidebar.nav.views.context.team.allFull';
    }
    return wfTitleId(false) ?? 'sidebar.nav.views.context.allFull';
  }

  return 'sidebar.nav.views.context.default';
}

export type ChatFolderColorTier = 'unassigned' | 'all' | 'mine' | 'other';

/** מדרג צבע לתיקייה פעילה — לא משויכות / כל השיחות / שלי / שאר התיקיות */
export function getChatFolderColorTier(folderTitle: ChatFolderTitle): ChatFolderColorTier {
  if (folderTitle.type === 'locale') {
    if (folderTitle.id === 'sidebar.nav.views.unassigned') return 'unassigned';
    if (folderTitle.id === 'sidebar.nav.chatScopeTabs.all') return 'all';
    if (folderTitle.id === 'sidebar.nav.chatScopeTabs.mine') return 'mine';
  }
  return 'other';
}

/** שם התיקייה הפעילה בסרגל — לפי נתיב, query ושיוך תצוגות מותאמות */
export function getChatActiveFolderTitle(
  pathname: string,
  search: string,
  viewFolderMap: ViewFolderMap,
  customFolders: ChatSidebarFolder[],
): ChatFolderTitle {
  if (pathname.startsWith('/chats/unassigned')) {
    return {
      type: 'locale',
      id: 'sidebar.nav.views.unassigned',
      defaultMessage: CHAT_VIEW_FOLDER_TITLE_DEFAULTS['sidebar.nav.views.unassigned'],
    };
  }

  const activeTabId = deriveActiveTabIdFromUrl(pathname, search, 'mine');

  const builtinFolder = (folderId: 'mine' | 'all'): ChatFolderTitle => ({
    type: 'locale',
    id: folderId === 'mine' ? 'sidebar.nav.chatScopeTabs.mine' : 'sidebar.nav.chatScopeTabs.all',
    defaultMessage:
      CHAT_VIEW_FOLDER_TITLE_DEFAULTS[
        folderId === 'mine' ? 'sidebar.nav.chatScopeTabs.mine' : 'sidebar.nav.chatScopeTabs.all'
      ],
  });

  if (activeTabId === 'mine') return builtinFolder('mine');
  if (activeTabId === 'team' || activeTabId === 'all') return builtinFolder('all');

  if (activeTabId.startsWith('custom:')) {
    const viewId = activeTabId.slice('custom:'.length);
    //* תצוגה בלי שיוך תיקייה (או ששויכה לתיקייה שנמחקה) שייכת לתיקיית המערכת
    //* «מותאמות» — לא ל«שלי» (שם תצוגות של משתמשים אחרים נראו כפריט זר).
    const folderId = viewFolderMap[viewId];
    if (!folderId) return CHAT_SIDEBAR_CUSTOM_VIEWS_FOLDER_TITLE;

    if (folderId === 'mine' || folderId === 'all') return builtinFolder(folderId);
    if (folderId === BUILTIN_UNASSIGNED_FOLDER_ID) {
      return {
        type: 'locale',
        id: 'sidebar.nav.views.unassigned',
        defaultMessage: CHAT_VIEW_FOLDER_TITLE_DEFAULTS['sidebar.nav.views.unassigned'],
      };
    }

    const customFolder = customFolders.find((f) => f.id === folderId);
    if (customFolder) return { type: 'text', text: customFolder.name };

    return CHAT_SIDEBAR_CUSTOM_VIEWS_FOLDER_TITLE;
  }

  return builtinFolder('mine');
}

export function chatSidebarBuiltinFolderTitle(scope: ChatSidebarScopeTab): ChatFolderTitle {
  const id =
    scope === 'mine'
      ? 'sidebar.nav.chatScopeTabs.mine'
      : scope === 'team'
        ? 'sidebar.nav.chatScopeTabs.team'
        : 'sidebar.nav.chatScopeTabs.all';
  return {
    type: 'locale',
    id,
    defaultMessage: CHAT_VIEW_FOLDER_TITLE_DEFAULTS[id] ?? '',
  };
}

export const CHAT_SIDEBAR_UNASSIGNED_FOLDER_TITLE: ChatFolderTitle = {
  type: 'locale',
  id: 'sidebar.nav.views.unassigned',
  defaultMessage: CHAT_VIEW_FOLDER_TITLE_DEFAULTS['sidebar.nav.views.unassigned'],
};

export const CHAT_SIDEBAR_BOTS_FOLDER_TITLE: ChatFolderTitle = {
  type: 'locale',
  id: 'sidebar.nav.views.botsFolder',
  defaultMessage: CHAT_VIEW_FOLDER_TITLE_DEFAULTS['sidebar.nav.views.botsFolder'],
};

/** תיקיית המערכת «מותאמות» — תצוגות מותאמות ללא שיוך תיקייה אצל המשתמש */
export const CHAT_SIDEBAR_CUSTOM_VIEWS_FOLDER_TITLE: ChatFolderTitle = {
  type: 'locale',
  id: 'sidebar.nav.views.customViewsFolder',
  defaultMessage: CHAT_VIEW_FOLDER_TITLE_DEFAULTS['sidebar.nav.views.customViewsFolder'],
};

export function resolveChatFolderLabel(title: ChatFolderTitle, intl: IntlShape): string {
  if (title.type === 'text') return title.text;
  return intl.formatMessage({
    id: title.id,
    defaultMessage: CHAT_VIEW_FOLDER_TITLE_DEFAULTS[title.id] ?? title.defaultMessage,
  });
}

/** שם תצוגה כמו בכותרת רשימת השיחות — לפי URL של פריט הסרגל */
export function resolveChatViewLabelFromNavItem(item: NavItemType, intl: IntlShape): string {
  if (item.noTranslation && item.title) return item.title;

  const raw = item.link || item.url;
  if (raw) {
    const pathPart = getPathnameOnly(raw);
    const qIdx = raw.indexOf('?');
    const search = qIdx >= 0 ? raw.slice(qIdx) : '';
    const viewTitleId = getChatViewContextTitleId(pathPart, search);
    return intl.formatMessage({
      id: viewTitleId,
      defaultMessage: CHAT_VIEW_CONTEXT_TITLE_DEFAULTS[viewTitleId] ?? 'שיחות',
    });
  }

  if (item.title) {
    return intl.formatMessage({ id: item.title, defaultMessage: item.title });
  }
  return '';
}
